import React from 'react';
import { Alert, Button, Text } from '@zillow/constellation-10';
import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';
import { getFrontEndInstance } from '@zg-rentals/rental-manager-env';

export class ErrorBoundary extends React.Component<React.PropsWithChildren<{ errorView?: React.ReactNode }>> {
  state = { hasError: false };

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    RpErrorBoundary.logError(error, errorInfo);
    this.setState({ hasError: true });
    const environment = getFrontEndInstance();
    environment.logger.fatal({
      context: 'ErrorBoundary',
      message: error.message,
      stack: error.stack,
    });
  }

  render() {
    const { children, errorView } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return (
        errorView ?? (
          <Alert tone="critical">
            <Text>Oops - something went wrong, please refresh the page and try again!</Text>
            <Button onClick={() => window.location.reload()} css={{ marginTop: 'sm' }}>
              Refresh Page
            </Button>
          </Alert>
        )
      );
    }

    return children;
  }
}

export function withErrorBoundary<P>(
  Component: React.ComponentType<P>,
): (props: P & { errorView?: React.ReactNode }) => React.ReactNode {
  const WithErrorBoundary = (props: P & { errorView?: React.ReactNode }) => {
    return (
      <ErrorBoundary errorView={props.errorView}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };

  WithErrorBoundary.displayName = `WithErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

  return WithErrorBoundary;
}

export default ErrorBoundary;
